import * as yup from "yup";
import {
  isValidPhoneNumber,
  isPossiblePhoneNumber,
} from "react-phone-number-input";
import { ISalon } from "../../interfaces/hosts";

export const isURLValid = (url: string): boolean => {
  const urlRegex = /(ftp|http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-\/]))?/;
  return urlRegex.test(url);
};

export const formSchema = yup.object().shape<any>({
  lat: yup.number(),
  lng: yup.number(),
  is_digital: yup.bool(),
  google_city_id: yup.string(),
  staff_free_text: yup.string(),
  comments_for_guests: yup.string(),
  org_branch: yup.string(),
  is_open_for_guests: yup.bool(),
  hosted_before: yup
    .bool()
    .required("host.hostProfile.validation.hosted_before"),
  evening_public: yup.bool(),
  apartment_number: yup
    .number()
    .nullable()
    .transform((value) => (Number.isNaN(value) ? null : value)),
  floor: yup
    .number()
    .nullable()
    .transform((value) => (Number.isNaN(value) ? null : value)),
  address: yup.string(),
  date: yup
    .date()
    .nullable()
    .required("host.hostProfile.validation.eventTimeRequired"),
  event_time: yup
    .string()
    .typeError("host.hostProfile.validation.eventTimeRequired")
    .required("host.hostProfile.validation.eventTimeRequired"),
  is_org: yup.bool(),
  contact_name: yup
    .string()
    .test(
      "match_name",
      "host.hostProfile.validation.contactNameRequired",
      function(name) {
        if (this.parent.is_org) {
          return name !== "";
        } else {
          return true;
        }
      }
    ),
  contact_phone: yup
    .string()
    .nullable()
    .test("match", "host.hostProfile.validation.contactPhoneNotValid", function(
      phone
    ) {
      if (this.parent.is_org) {
        if (!phone) return false;
        if (phone && phone.length < 8) return false;
        if (!isValidPhoneNumber(phone)) return false;
        if (!isPossiblePhoneNumber(phone)) return false;
        return true;
      } else {
        return true;
      }
    }),
  language: yup
    .number()
    .required("host.hostProfile.validation.languageRequired"),
  city_name: yup.string(),
  accessibility: yup.array().of(yup.string()),
  survivor_needed: yup
    .bool()
    .required("host.hostProfile.validation.survivorNeededRequired"),
  max_guests: yup
    .number()
    .positive("admin.addWitness.validation.mustBePositive")
    .typeError("admin.addWitness.validation.maxGuestsRequired")
    .lessThan(1000, "admin.addWitness.validation.maxGuestsMaxValue")
    .test(
      "match_guests",
      "admin.addWitness.validation.maxGuestsRequired",
      function(maxGuests) {
        return true;
      }
    ),
  site_link: yup
    .string()
    .typeError("formFields.siteLinkValidationError")
    .test("match", "formFields.siteLinkValidationError", function(site_link) {
      if (site_link && site_link !== "" && site_link !== null) {
        return isURLValid(site_link);
      } else {
        return true;
      }
    }),
  host_salon_training: yup
    .string()
    .nullable()
    .required("register.hostTrainingIsRequired"),
});

export const salonDetailsMap = (salon, t, langDirection) => {
  if (!salon) return "";
  const cleanAddress = salon.address || "";
  const includesComma = cleanAddress.includes(",");
  const cityIncluded = salon.city_name && cleanAddress.includes(salon.city_name);

  const addressWithCity = cleanAddress
    ? (includesComma || cityIncluded
        ? cleanAddress
        : `${cleanAddress}${salon.city_name ? `, ${salon.city_name}` : ""}`)
    : salon.city_name || "";

  const hasFloor =(salon.floor === 0 || !!salon.floor) && salon.floor !== 9999;
  const hasApartment =(salon.apartment_number === 0 || !!salon.apartment_number) &&salon.apartment_number !== 9999;
  const hasAddressContent = addressWithCity || hasFloor || hasApartment;
  const fullAddress = hasAddressContent
  
  ? `${addressWithCity}${
      hasFloor ? `, ${t("host.hostProfile.floor")} ${salon.floor}` : ""
    }${
      hasApartment ? ` ${t("host.hostProfile.apartment")} ${salon.apartment_number}` : ""
    }`
  : salon.distance
    ? langDirection === "rtl"
      ? `${salon.distance} ק״מ ממך`
      : `${salon.distance} km from you`
    : "";


  return fullAddress;
};

export const salonDetailsMapCard = (selectedSalonDetails, t, langDirection) => {
  if (!selectedSalonDetails) return "";

  const cleanAddress = selectedSalonDetails.address || "";
  const includesComma = cleanAddress.includes(",");
  const cityIncluded =
    selectedSalonDetails.city_name &&
    cleanAddress.includes(selectedSalonDetails.city_name);

  const addressWithCity = cleanAddress
    ? includesComma || cityIncluded
      ? cleanAddress
      : `${cleanAddress}${
          selectedSalonDetails.city_name
            ? `, ${selectedSalonDetails.city_name}`
            : ""
        }`
    : selectedSalonDetails.city_name || "";

  const hasFloor =
    (selectedSalonDetails.floor === 0 || !!selectedSalonDetails.floor) &&
    selectedSalonDetails.floor !== 9999;

  const hasApartment =
    (selectedSalonDetails.apartment_number === 0 ||
      !!selectedSalonDetails.apartment_number) &&
    selectedSalonDetails.apartment_number !== 9999;

  const hasAddressContent = addressWithCity || hasFloor || hasApartment;

  const fullAddress = hasAddressContent
    ? `${addressWithCity}${
        hasFloor
          ? `, ${t("host.hostProfile.floor")} ${selectedSalonDetails.floor}`
          : ""
      }${
        hasApartment
          ? ` ${t("host.hostProfile.apartment")} ${selectedSalonDetails.apartment_number}`
          : ""
      }${
        selectedSalonDetails.house_number
          ? ` ${t("formFields.floor")} ${selectedSalonDetails.house_number}`
          : ""
      }`
    : selectedSalonDetails.distance
    ? langDirection === "rtl"
      ? `${selectedSalonDetails.distance} ק״מ ממך`
      : `${selectedSalonDetails.distance} km from you`
    : "";

  return fullAddress;
};